import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TrackingResult from './components/TrackingResult';
import { mockOrders } from '../../mocks/orders';
import type { MockOrder } from '../../mocks/orders';

const SAMPLE_ORDERS = ['56805', '72341', '89012'];

export default function TrackOrderPage() {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<MockOrder | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [searched, setSearched] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const id = query.trim().replace('#', '');
    if (!id) return;
    setSearched(true);
    const found = mockOrders[id];
    if (found) {
      setResult(found);
      setNotFound(false);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } else {
      setResult(null);
      setNotFound(true);
    }
  };

  const handleSample = (id: string) => {
    setQuery(id);
    const found = mockOrders[id];
    if (found) {
      setResult(found);
      setNotFound(false);
      setSearched(true);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  };

  return (
    <main dir="rtl">
      <Navbar />

      {/* Header */}
      <section className="pt-28 pb-10 px-6 lg:px-12 bg-[#FAFAF8] border-b border-gray-100">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-[#C9A961]/10 rounded-2xl mx-auto mb-4">
            <i className="ri-truck-line text-3xl text-[#C9A961]" />
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-2">تتبّع طلبك</h1>
          <p className="text-gray-400 text-sm">أدخل رقم الطلب الذي استلمته عند تسجيل الطلب لمعرفة حالته الآن</p>
        </div>
      </section>

      {/* Search section */}
      <section className="py-10 px-6 lg:px-12 bg-[#F5F1E8] min-h-screen">
        <div className="max-w-2xl mx-auto">

          {/* Search card */}
          <div className="bg-white rounded-2xl p-6 mb-6">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1 relative">
                <div className="absolute top-1/2 -translate-y-1/2 right-4 w-5 h-5 flex items-center justify-center pointer-events-none">
                  <i className="ri-search-line text-gray-400 text-lg" />
                </div>
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="أدخل رقم الطلب مثل: 56805"
                  className="w-full pr-11 pl-4 py-3.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 focus:border-[#C9A961]/50 bg-[#FAFAF8] text-gray-800"
                  dir="ltr"
                />
              </div>
              <button
                type="submit"
                className="bg-[#C9A961] hover:bg-[#A07C30] text-white px-6 py-3.5 rounded-xl font-black text-sm cursor-pointer transition-colors whitespace-nowrap"
              >
                تتبّع الطلب
              </button>
            </form>

            {/* Quick samples */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400 mb-2">جرّب أحد هذه الأرقام للتجربة:</p>
              <div className="flex gap-2 flex-wrap">
                {SAMPLE_ORDERS.map(id => (
                  <button
                    key={id}
                    onClick={() => handleSample(id)}
                    className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#F5F1E8] text-[#C9A961] hover:bg-[#C9A961] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                  >
                    #{id}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Not found */}
          {searched && notFound && (
            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
                <i className="ri-search-line text-3xl text-red-400" />
              </div>
              <h3 className="text-lg font-black text-gray-800 mb-2">لم نجد هذا الطلب</h3>
              <p className="text-gray-500 text-sm mb-5 leading-relaxed">
                تأكد من رقم الطلب وأعد المحاولة.<br />
                رقم الطلب موجود في رسالة التأكيد التي وصلتك.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => { setQuery(''); setSearched(false); setNotFound(false); }}
                  className="px-6 py-3 bg-[#C9A961] text-white rounded-xl font-bold text-sm cursor-pointer hover:bg-[#A07C30] transition-colors whitespace-nowrap"
                >
                  حاول مرة أخرى
                </button>
                <Link
                  to="/contact"
                  className="px-6 py-3 border border-gray-200 text-gray-600 rounded-xl font-bold text-sm cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-customer-service-line" />
                  </div>
                  تواصل مع الدعم
                </Link>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div ref={resultRef}>
              <TrackingResult order={result} />
            </div>
          )}

          {/* Info boxes (shown when no result yet) */}
          {!searched && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
              {[
                { icon: 'ri-shield-check-line', title: 'بدون تسجيل', desc: 'تتبّع طلبك فوراً بدون الحاجة لتسجيل الدخول' },
                { icon: 'ri-time-line', title: 'تحديث فوري', desc: 'حالة طلبك تُحدّث في الوقت الفعلي' },
                { icon: 'ri-customer-service-2-line', title: 'دعم 24/7', desc: 'فريقنا متاح دائماً للمساعدة عبر واتساب' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 text-center">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#C9A961]/10 rounded-xl mx-auto mb-3">
                    <i className={`${item.icon} text-xl text-[#C9A961]`} />
                  </div>
                  <p className="text-sm font-black text-gray-800 mb-1">{item.title}</p>
                  <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          )}

        </div>
      </section>

      <Footer />
    </main>
  );
}
